import React, { useMemo, useState } from 'react';
import { useGlobal } from 'reactn';
import { Alert, Button, Card, DatePicker, Select, Switch, Typography } from 'antd';
import moment from 'moment';
import { getCurrentWeekEndDate } from '../../common';

const { Title, Text } = Typography;

const MODES = [
  { value: 'friday', label: 'Friday (Current Week Reminder)' },
  { value: 'monday', label: 'Monday (Late Reminder)' },
  { value: 'tuesday', label: 'Tuesday (Final Warning)' },
];

const NotificationsPage = () => {
  const [user] = useGlobal('user');
  const [mode, setMode] = useState('friday');
  const [endDate, setEndDate] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const defaultEndDate = useMemo(() => {
    const currentWeekEnd = moment(getCurrentWeekEndDate(), 'YYYYMMDD');
    if (currentWeekEnd.isAfter(moment(), 'day')) {
      return currentWeekEnd.subtract(1, 'week');
    }
    return currentWeekEnd;
  }, []);

  const resolvedEndDate = endDate || defaultEndDate;
  const callerEmail = user?.emails?.[0]?.address || '';
  const apiBaseUrl = process?.env?.REACT_APP_GRAPHQL_BASE_URL || '';

  const callApi = async (action) => {
    const body = {
      mode,
      dryRun,
      endDate: resolvedEndDate.format('YYYYMMDD'),
      action,
    };
    const response = await fetch(`${apiBaseUrl}/api/reminders/timesheet`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error || 'Request failed');
    }
    return data;
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setError(null);
    setResult(null);
    setPreview(null);
    try {
      const data = await callApi('preview');
      setPreview(data);
    } catch (err) {
      setError(err.message || 'Unexpected error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await callApi('send');
      setResult(data);
      setPreview(null);
    } catch (err) {
      setError(err.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPreview = () => {
    setPreview(null);
  };

  return (
    <div className="p-6">
      <Title level={3}>Notifications</Title>
      <Text type="secondary">
        Trigger timesheet reminder emails manually. Dry-run sends only to you.
      </Text>

      <Card className="mt-4" title="Timesheet Reminders">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Text strong>Mode</Text>
            <Select className="w-full mt-1" value={mode} onChange={setMode}>
              {MODES.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Text strong>End Date</Text>
            <DatePicker
              className="w-full mt-1"
              value={resolvedEndDate}
              onChange={setEndDate}
              format="YYYY-MM-DD"
            />
            <div className="mt-1 text-xs text-gray-500">
              Default: {defaultEndDate.format('YYYY-MM-DD')}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={dryRun} onChange={setDryRun} />
            <Text>Dry-run (send only to me)</Text>
          </div>
        </div>

        <div className="mt-4">
          <Button type="primary" onClick={handlePreview} loading={previewing}>
            Preview Recipients
          </Button>
        </div>

        {error && (
          <Alert className="mt-4" type="error" showIcon message="Error" description={error} />
        )}

        {preview && (
          <Card className="mt-4" size="small" title="Preview — Recipients">
            <div className="mb-3">
              <div><Text strong>Mode:</Text> {preview.mode}</div>
              <div><Text strong>Week ending:</Text> {preview.endDateFormatted}</div>
              <div><Text strong>Dry Run:</Text> {preview.dryRun ? 'Yes' : 'No'}</div>
              {preview.dryRun && (
                <div><Text strong>Actual recipient (dry-run):</Text> {callerEmail}</div>
              )}
            </div>

            <div className="mb-3">
              <Text strong>
                Users who have not submitted ({preview.allBccEmails?.length || 0}):
              </Text>
              <div className="mt-1 max-h-64 overflow-y-auto rounded border p-2 text-xs">
                {preview.allBccEmails?.length > 0 ? (
                  preview.allBccEmails.map((email, i) => <div key={i}>{email}</div>)
                ) : (
                  <Text type="secondary">No recipients found for this period.</Text>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="primary"
                onClick={handleConfirmSend}
                loading={loading}
                danger={!dryRun}
              >
                {dryRun ? 'Send (dry-run)' : `Send to all ${preview.allBccEmails?.length || 0} recipients`}
              </Button>
              <Button onClick={handleCancelPreview}>Cancel</Button>
            </div>
          </Card>
        )}

        {result && (
          <Alert
            className="mt-4"
            type="success"
            showIcon
            message="Sent"
            description={
              <div>
                <div>Mode: {result.mode}</div>
                <div>End Date: {result.endDate}</div>
                <div>Dry Run: {String(result.dryRun)}</div>
                <div>Sent Count: {result.sentCount}</div>
              </div>
            }
          />
        )}
      </Card>
    </div>
  );
};

export default NotificationsPage;
